import React, { useEffect, useState } from "react";
import { css } from "@emotion/css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiCalendar } from "react-icons/fi";
import Header from "../Header";
import Footer from "../Footer";
import { getUserKey } from "../../utils/auth";
import { config } from "../../config";
import Loader from "../../common/loader/Loader";
import EventDetails from "./EventDetails";

// ------ Styles ------
const dashboardContainerStyle = css`
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background-color: #f4f4f9;
`;

const mainContentStyle = css`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 24px 16px;
    max-width: 1000px;
    margin: 0 auto;
    width: 100%;
    min-height: calc(100vh - 75px);
    @media (min-width: 768px) {
        padding: 40px 24px;
    }
`;

const welcomeTextStyle = css`
    font-size: 18px;
    font-weight: 600;
    color: #49255d;
    margin-bottom: 24px;
    text-align: center;
`;

const sectionHeaderStyle = css`
    font-size: 24px;
    font-weight: 700;
    color: #333;
    text-align: center;
    margin-bottom: 8px;
`;

const dividerStyle = css`
    width: 128px;
    height: 6px;
    background-color: #f5b940;
    margin: 0 auto 32px auto;
    border-radius: 3px;
`;

const eventsContainerStyle = css`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const emptyStateCardStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: white;
    border-radius: 12px;
    box-shadow:
        0 10px 15px -3px rgba(0, 0, 0, 0.05),
        0 4px 6px -2px rgba(0, 0, 0, 0.025);
    padding: 32px 16px;
    min-height: 250px;
    border: 1px solid #f0f0f0;

    @media (min-width: 768px) {
        min-height: 300px;
        padding: 48px 24px;
    }
`;

const emptyIconStyle = css`
    font-size: 48px;
    color: #f5b940;
    margin-bottom: 20px;
    opacity: 0.8;
`;

const emptyTextStyle = css`
    font-size: 18px;
    color: #6b7280;
    font-weight: 500;
    text-align: center;
    @media (min-width: 768px) {
        font-size: 20px;
    }
`;

// ------ Main Dashboard ------
export default function Dashboard() {
    const navigate = useNavigate();
    const userData = getUserKey();

    const [eventData, setEventData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const payload = {
        user_id: userData?.id,
        member_id: userData?.member_id,
        country_id: userData?.country_id,
    };

    const fetchEvents = async () => {
        setIsLoading(true);
        try {
            const token = userData?.token || "";
            const headers = {
                "Content-Type": "application/json",
            };

            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }

            const response = await fetch(
                `${config.serverUrl}/api/public/events?upcoming=true`,
                {
                    method: "GET",
                    headers,
                },
            );

            const result = await response.json();

            if (response.ok && result?.status === "success") {
                setEventData(result.response || []);
            } else {
                toast.error(result?.message || "Failed to load events.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong loading events.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (userData) {
            fetchEvents();
        }
    }, []);

    if (!userData) return null;

    return (
        <div className={dashboardContainerStyle}>
            <Header />

            <main className={mainContentStyle}>
                <h2 className={welcomeTextStyle}>
                    Welcome {userData.firstName} {userData.lastName} (
                    {userData.memberId})
                </h2>

                <section>
                    <h3 className={sectionHeaderStyle}>Upcoming Events</h3>
                    <div className={dividerStyle}></div>

                    {isLoading ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                padding: "40px",
                            }}
                        >
                            <Loader show={true} />
                        </div>
                    ) : (
                        <div className={eventsContainerStyle}>
                            {eventData.length === 0 ? (
                                <div className={emptyStateCardStyle}>
                                    <FiCalendar className={emptyIconStyle} />
                                    <p className={emptyTextStyle}>
                                        No upcoming events scheduled
                                    </p>
                                </div>
                            ) : (
                                eventData.map((event) => (
                                    <EventDetails
                                        key={event.id}
                                        event={event}
                                        fetchEvents={fetchEvents}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </section>
            </main>

            <Footer />
        </div>
    );
}
